const URL_QUERY_OR_FRAGMENT_PATTERN = /[?#]/

// Caption track shapes shared by the composer and every Studio preview.
// Composer state keeps `captionTracks` (with ids and file names), a submitted
// media object keeps `captions`, stored media keeps `metadata.captions`, and a
// feed item keeps `playback.captions` — the only key the widget reads.

export function captionFileName(url) {
  const path = String(url || '').split(URL_QUERY_OR_FRAGMENT_PATTERN)[0]
  try {
    return decodeURIComponent(path.split('/').pop() || '')
  } catch {
    return path.split('/').pop() || ''
  }
}

// Reads stored tracks (metadata.captions, a submitted `captions` list or a
// draft's `captionTracks`) into plain track records.
export function normalizeCaptionTracks(raw) {
  if (!Array.isArray(raw)) return []
  return raw
    .filter((track) => track && typeof track === 'object' && String(track.url || '').trim())
    .map((track) => ({
      url: String(track.url).trim(),
      objectKey: track.objectKey || track.object_key || null,
      fileName: track.fileName || captionFileName(track.url),
      lang: String(track.lang || '').trim().toLocaleLowerCase(),
      label: String(track.label || '').trim(),
      kind: String(track.kind || '').toLocaleLowerCase() === 'captions' ? 'captions' : 'subtitles',
    }))
}

export function serializeCaptionTracks(tracks = []) {
  return (tracks || []).map((track) => {
    const lang = String(track.lang || '').trim().toLocaleLowerCase()
    const label = String(track.label || '').trim()
    return {
      url: track.url,
      lang,
      ...(label ? { label } : {}),
      kind: track.kind === 'captions' ? 'captions' : 'subtitles',
    }
  })
}

function isAbsoluteHttpUrl(value) {
  try {
    const parsed = new URL(value)
    return (parsed.protocol === 'https:' || parsed.protocol === 'http:') && Boolean(parsed.host)
  } catch {
    return false
  }
}

const PREVIEW_CAPTION_SOURCES = [
  (media) => media.captionTracks,
  (media) => media.captions,
  (media) => media.metadata?.captions,
  (media) => media.playback?.captions,
]

// Feed-shaped tracks for a preview. The first source that is an array wins,
// even when it is empty, so composer state (where removing the last track
// leaves `[]`) never falls back to stale stored metadata. Tracks without a
// language or an absolute http(s) URL are not previewed: the widget would
// drop them too.
export function previewCaptionTracks(media) {
  if (!media || typeof media !== 'object') return []
  let source = []
  for (const read of PREVIEW_CAPTION_SOURCES) {
    const candidate = read(media)
    if (Array.isArray(candidate)) {
      source = candidate
      break
    }
  }
  return serializeCaptionTracks(
    normalizeCaptionTracks(source).filter((track) => track.lang && isAbsoluteHttpUrl(track.url)),
  )
}
